<template>
  <button 
    v-if="hasActiveSession"
    class="waiter-call-bell"
    :class="{
      'waiter-call-bell--sending': feedbackState === 'sending',
      'waiter-call-bell--sent': feedbackState === 'sent',
      'waiter-call-bell--error': feedbackState === 'error',
      'waiter-call-bell--inactive': !canCallWaiter
    }"
    :disabled="isSending || feedbackState === 'sent' || !canCallWaiter"
    :aria-label="canCallWaiter ? 'Llamar al mesero' : 'Activa una orden primero'"
    :title="canCallWaiter ? 'Llama a tu mesero' : 'Activa una orden primero'"
    type="button"
    @click="callWaiter"
  >
    <svg 
      class="waiter-call-bell__icon" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      stroke-width="2"
      aria-hidden="true"
    >
      <path 
        d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" 
        stroke-linecap="round" 
        stroke-linejoin="round"
      />
      <path 
        d="M13.73 21a2 2 0 0 1-3.46 0" 
        stroke-linecap="round" 
        stroke-linejoin="round"
      />
    </svg>
    
    <span class="waiter-call-bell__text">
      {{ buttonText }}
    </span>
    
    <span 
      v-if="feedbackState === 'sent'" 
      class="waiter-call-bell__badge"
      aria-label="Mesero en camino"
    >
      ✓
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useSessionStore } from '../../stores/session-store';
import { useOrdersStore } from '../../stores/orders-store';
import { HTTPError, requestJSON } from '../../core/http';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '../../composables/use-notifications';

const sessionStore = useSessionStore();
const ordersStore = useOrdersStore();
const notifications = useNotifications();
const { t } = useI18n();

type FeedbackState = 'idle' | 'sending' | 'sent' | 'error';

const feedbackState = ref<FeedbackState>('idle');

const hasActiveSession = computed(() => {
  return sessionStore.isAuthenticated && sessionStore.sessionId;
});

const hasActiveOrder = computed(() => {
  return ordersStore.hasActiveOrders;
});

const canCallWaiter = computed(() => {
  return hasActiveSession.value && hasActiveOrder.value;
});

const isSending = computed(() => {
  return feedbackState.value === 'sending';
});

const buttonText = computed(() => {
  switch (feedbackState.value) {
    case 'sending':
      return t('waiter.call.sending');
    case 'sent':
      return t('waiter.call.sent');
    case 'error':
      return t('waiter.call.error');
    default:
      return t('waiter.call.button');
  }
});

const callWaiter = async () => {
  if (!canCallWaiter.value || isSending.value || feedbackState.value === 'sent') {
    return;
  }

  feedbackState.value = 'sending';

  try {
    const tableId = sessionStore.tableContext?.table_id;
    
    await requestJSON(`/api/v1/dining-sessions/${encodeURIComponent(String(sessionStore.sessionId))}/waiter-call`, {
      method: 'POST',
      body: {
        table_id: tableId,
        session_id: sessionStore.sessionId,
      },
    });

    feedbackState.value = 'sent';
    notifications.success(t('waiter.call.sent_toast', 'Tu mesero ha sido notificado y viene en camino.'));

    // Reset after 5 seconds to prevent spamming
    setTimeout(() => {
      if (feedbackState.value === 'sent') {
        feedbackState.value = 'idle';
      }
    }, 5000);
  } catch (error) {
    console.error('Failed to call waiter:', error);
    feedbackState.value = 'error';

    const isRateLimit = error instanceof HTTPError && error.statusCode === 429;
    if (isRateLimit) {
      notifications.info(t('waiter.call.rate_limit_toast', 'Tu llamado al mesero ya está activo. Por favor espera un momento.'));
    } else {
      notifications.error(t('waiter.call.error_toast', 'No se pudo llamar al mesero. Por favor intenta de nuevo.'));
    }
    
    // Reset error after 3 seconds
    setTimeout(() => {
      if (feedbackState.value === 'error') {
        feedbackState.value = 'idle';
      }
    }, 3000);
  }
};
</script>

<style scoped>
.waiter-call-bell {
  position: fixed;
  bottom: 20px;
  right: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background-color: #e05a2a;
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(255, 107, 53, 0.4);
  transition: all 0.2s ease;
  z-index: 1000;
}

.waiter-call-bell:hover:not(:disabled) {
  background-color: #ff5722;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(255, 107, 53, 0.5);
}

.waiter-call-bell:active:not(:disabled) {
  transform: translateY(0);
}

.waiter-call-bell--inactive {
  opacity: 0.45;
  cursor: not-allowed;
  background-color: #94a3b8;
  box-shadow: 0 2px 8px rgba(148, 163, 184, 0.3);
}

.waiter-call-bell--inactive:hover {
  background-color: #94a3b8 !important;
  transform: none !important;
}

.waiter-call-bell:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.waiter-call-bell__icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.waiter-call-bell__text {
  white-space: nowrap;
}

.waiter-call-bell__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: white;
  color: #e05a2a;
  border-radius: 50%;
  font-weight: bold;
  font-size: 14px;
  animation: badge-pop 0.3s ease;
}

/* Sending state */
.waiter-call-bell--sending {
  animation: bell-pulse 1s ease-in-out infinite;
}

.waiter-call-bell--sending .waiter-call-bell__icon {
  animation: bell-ring 0.5s ease-in-out infinite;
}

/* Sent state */
.waiter-call-bell--sent {
  background-color: #4caf50;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
}

.waiter-call-bell--sent:hover:not(:disabled) {
  background-color: #43a047;
}

/* Error state */
.waiter-call-bell--error {
  background-color: #f44336;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.4);
}

.waiter-call-bell--error:hover:not(:disabled) {
  background-color: #e53935;
}

/* Animations */
@keyframes bell-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes bell-ring {
  0%, 100% {
    transform: rotate(0);
  }
  25% {
    transform: rotate(-10deg);
  }
  75% {
    transform: rotate(10deg);
  }
}

@keyframes badge-pop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Mobile responsive */
@media (max-width: 768px) {
  .waiter-call-bell {
    bottom: 16px;
    right: 16px;
    padding: 10px 16px;
    font-size: 14px;
  }

  .waiter-call-bell__icon {
    width: 20px;
    height: 20px;
  }
}
</style>
